import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDown, CheckCircle2, CircleAlert } from 'lucide-react';
import { api } from '@/services/api';
import { CLAIMS_QUERY_KEY } from '@/components/expenses/ExpenseLedger';
import { claimMatches, needsAttention, type CategoryScope, type ClaimScope } from '@/lib/bills-display';
import { nextSignaturePayment, paidToFamily } from '@/lib/grants-status';
import { deriveRenewalCta, renewalCtaFromError } from '@/lib/renewal-display';
import { RENEWAL_MINE_QUERY_KEY } from '@/lib/wizardDataSource';
import { formatMoney, monthLabel } from '@/lib/money';
import type { GrantRequest, ReimbursementClaim } from '@/types/api';

/** The /bills toolbar's receipt filters, as the ledger applies them (#2493). */
export interface StatusLineReceiptFilters {
  search: string;
  claimScope: ClaimScope;
  categoryScope: CategoryScope;
}

/**
 * The first line of `/bills` (2026-10-03): the verdict, then the money.
 *
 * Victor: "I came here trying to figure out … everything good? Do I need any
 * action? Where is my money?" The page used to open on a renewal pitch and a
 * to-do list, and answered those three only by scrolling. This line answers
 * them in one reading:
 *
 *   All on track  ·  $2,000 paid to you so far  ·  Next payment: November 2026
 *   2 receipts need you ↓  ·  …
 *
 * Reads the two queries the page already holds (same keys), so it costs no
 * request. Renders nothing until both settle — a verdict that flips from
 * "All on track" to "2 receipts need you" a beat later is worse than none.
 *
 * The verdict is renewal-aware (2026-10-11 walkthrough). It decided from
 * receipts alone, so "All on track" sat in green beside a renewal TRF had sent
 * back for more information, and beside a declined one. It now reads the
 * renewal the page's renewal line already reads (same key, a cache hit):
 *   - needs_info → "Your renewal needs you ↓", in the warn tone, to the line.
 *   - declined   → no verdict at all. The renewal line says it was not
 *                  approved (DECISIONS 2026-10-11); green beside it
 *                  contradicts it, and a red word here would overrule it.
 *   - waitlisted → no verdict either (DECISIONS 2026-10-11): "All on track"
 *                  in green beside "your renewal is on the waitlist" reads
 *                  as the renewal being settled when it is not.
 *   - unknown    → no verdict: a failed read is not evidence of "on track".
 *
 * The receipts count follows the toolbar (#2493). /bills passes its search and
 * receipt filters, and the count is the attention receipts the ledger still
 * shows under them, through the ledger's own predicate (`claimMatches`, the
 * one #2407's section counts use). It said "2 receipts need you" while the
 * list under it read "No receipts match." With nothing filtered the count is
 * every attention receipt, as before. A filtered view that hides every
 * attention receipt shows no verdict: "All on track" would be a claim about
 * receipts the filter is hiding.
 */
export function GrantsStatusLine({
  todoAnchor,
  renewalAnchor,
  filters,
}: {
  todoAnchor: string;
  renewalAnchor?: string;
  /** The page toolbar's receipt filters and search; absent, every receipt counts. */
  filters?: StatusLineReceiptFilters;
}) {
  const { t } = useTranslation('grants');
  const grantsQuery = useQuery({ queryKey: ['grants-mine'], queryFn: () => api.grants.mine() });
  const claimsQuery = useQuery({ queryKey: CLAIMS_QUERY_KEY, queryFn: () => api.reimbursements.listMine() });
  const renewalQuery = useQuery({
    queryKey: RENEWAL_MINE_QUERY_KEY,
    queryFn: () => api.renewals.getMine(),
    retry: false,
  });
  if (!grantsQuery.isSuccess || !claimsQuery.isSuccess || renewalQuery.isPending) return null;
  const renewal = renewalQuery.isError
    ? renewalCtaFromError(renewalQuery.error)
    : deriveRenewalCta(renewalQuery.data?.data ?? null, (grantsQuery.data?.data ?? []) as GrantRequest[]);
  const renewalNeedsYou = renewal.kind === 'needs_info';
  const verdictWithheld = renewal.kind === 'declined' || renewal.kind === 'waitlisted' || renewal.kind === 'unknown';

  const grants = (grantsQuery.data?.data ?? []) as GrantRequest[];
  const claims = (claimsQuery.data?.data ?? []) as ReimbursementClaim[];
  const now = new Date();
  const attention = claims.filter((c) => needsAttention(c, now));
  const todo = filters
    ? attention.filter((c) => claimMatches(c, filters.claimScope, filters.categoryScope, filters.search, now)).length
    : attention.length;
  // "All on track" speaks for every receipt, so it needs none waiting, hidden or not.
  const allClear = attention.length === 0;
  const paid = paidToFamily(grants, claims);
  const next = nextSignaturePayment(grants, now);

  const nextText = !next
    ? null
    : next.kind === 'paused'
      ? t('page.status.paused')
      : next.kind === 'due'
        ? t('page.status.paymentDue', { month: monthLabel(next.ym) ?? next.ym })
        : t('page.status.nextPayment', { month: monthLabel(next.ym) ?? next.ym });

  // A payment the record shows as due (payments recorded, the latest month
  // missing) is not "on track": the line already says "due since <month>", and
  // green beside it contradicts it. Withheld, like a declined renewal, not
  // turned red. D-2394 still applies: zero recorded payments names no due month,
  // so that grant keeps "All on track" (2026-11-01 sweep, 10 of 11 months paid).
  const paymentDue = next?.kind === 'due';
  // Nothing to say: no verdict, no money line. An empty status region is noise.
  const showOk = todo === 0 && allClear && !renewalNeedsYou && !verdictWithheld && !paymentDue;
  if (!renewalNeedsYou && todo === 0 && !showOk && !(paid && paid.cents > 0) && !nextText) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body"
      data-testid="grants-status-line"
      role="status"
    >
      {renewalNeedsYou && (
        <a
          href={renewalAnchor ? `#${renewalAnchor}` : undefined}
          className="inline-flex items-center gap-1.5 font-medium text-status-warn-fg hover:underline"
          data-testid="status-renewal"
        >
          <CircleAlert className="h-4 w-4" aria-hidden="true" />
          {t('page.status.renewalNeedsYou')}
          {renewalAnchor && <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />}
        </a>
      )}
      {todo > 0 ? (
        <a
          href={`#${todoAnchor}`}
          className="inline-flex items-center gap-1.5 font-medium text-status-warn-fg hover:underline"
          data-testid="status-todo"
        >
          <CircleAlert className="h-4 w-4" aria-hidden="true" />
          {t('page.status.todo', { count: todo })}
          <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      ) : !showOk ? null : (
        <span className="inline-flex items-center gap-1.5 font-medium text-status-ok-fg" data-testid="status-ok">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          {t('page.status.allGood')}
        </span>
      )}
      {paid && paid.cents > 0 && (
        <span className="text-stevens-ink-2" data-testid="status-paid">
          {t('page.status.paid', { amount: formatMoney(paid.cents, paid.currency) })}
        </span>
      )}
      {nextText && <span className="text-stevens-ink-2" data-testid="status-next">{nextText}</span>}
    </div>
  );
}
